import React, { useEffect, useState } from "react";
import { HashRouter, BrowserRouter, Route, Switch, Redirect, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { importNewModal } from "./redux/actions/newModal";
import {
  createClient,
  dedupExchange,
  cacheExchange,
  fetchExchange,
  Provider,
  defaultExchanges,
  subscriptionExchange,
} from "urql";
import { authExchange } from "@urql/exchange-auth";
import {
  getAuth,
  addAuthToOperation,
  didAuthError,
  willAuthError,
} from "./graphql/logIn";
import { SubcribeWS } from './graphql/botsWs'
import { graphqlWsClient, reconnectGraphqlWsClient } from "./graphql/wsClient";
import { setUrqlTenantCompanyId } from "./graphql/tenantCompanyId";
import { tenantCompanyExchange } from "./graphql/tenantUrqlExchange";
import "./scss/style.scss";
import Alert from "./components/alert";
import Modal from "./components/modal";
import NewModal from "./components/flujocaja(new)/newModal";
import Intercom from "react-intercom";
import PepperLogo from "./assets/svg/Pepper/Logo_Pepper_Horizontal.svg";
import { Plans } from "./utils/plans";
import { UserContext } from "./context";
import { useContext } from "react";
import { Roles } from "./utils/roles";
import WelcomeModal from "./containers/welcomeModal";
import { rutWithDash } from "./utils/formatter";
import moment from "moment";
import { Holding } from "./components/holding/holding";
import { identifyHeapUser, syncHeapCompany } from "./utils/heapAnalytics";

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"></div>
  </div>
);

const Layout = React.lazy(() => import("./containers/Layout"));
//const Login = React.lazy(() => import("./components/login/Login"));
const SignIn = React.lazy(() => import("./components/sign_in/sign_in"));
const RepositoryFileAccess = React.lazy(() => import("./components/repositorio/RepositoryFileAccess"));
const WorkerInviteWizard = React.lazy(() =>
  import("./components/cargarNuevoEmpleado/WorkerInviteWizard")
);
// const SignIn = React.lazy(() => import("./components/newOnboarding/SignIn"));
const SignUp = React.lazy(() => import("./components/newOnboarding/SignUp"));
const CreateCompany = React.lazy(() => import("./components/newOnboarding/CreateCompany"));
const ConnectSII = React.lazy(() => import("./components/newOnboarding/ConnectSII"));
const ConnectBalance = React.lazy(() => import("./components/newOnboarding/ConnectBalance"));
const CashFlowQuestions = React.lazy(() => import("./components/newOnboarding/CashFlowQuestions"));
const OnboardingWaitingRoom = React.lazy(() => import("./components/newOnboarding/OnboardingWaitingRoom"));
const ForgotPassword = React.lazy(() => import("./components/sign_in/forgotPassword"));
const CreatePassword = React.lazy(() => import("./components/sign_in/createPassword"));
const ResetPassword = React.lazy(() => import("./components/newOnboarding/ResetPassword"));
const OnboardingSCFO = React.lazy(() => import("./components/onboarding_scfo/index"));
const DirectOnboarding = React.lazy(() => import("./components/onboarding_scfo_direct/index"));
const SiiOnboarding = React.lazy(() => import("./components/onboarding_sii/index"));
const AutoLogin = React.lazy(() => import("./components/sign_in/autoLogin"));
const RedirectToSignin = React.lazy(() => import("./components/sign_in/RedirectToSignin"));

const client = createClient({
  url: process.env.REACT_APP_API_URL,
  fetchOptions: {
    credentials: "include",
  },
  exchanges: [
    // subscriptionExchange PRIMERO: si va después de dedup/cache, los eventos en vivo no llegan al UI.
    subscriptionExchange({
      forwardSubscription: (operation) => {
        const input = {
          query: operation.query || "",
          variables: operation.variables,
        };
        return {
          subscribe: (sink) => {
            const unsubscribe = graphqlWsClient.subscribe(input, sink);
            return { unsubscribe };
          },
        };
      },
    }),
    tenantCompanyExchange(),
    dedupExchange,
    cacheExchange,
    authExchange({
      addAuthToOperation,
      willAuthError,
      didAuthError,
      getAuth,
    }),
    fetchExchange,
  ],
});

function App() {
  const { uid, name, email, surname, roles, phone } = useSelector((state) => state.auth);
  const { showModal } = useSelector((state) => state.newModal);
  const { selectedCompany } = useSelector((state) => state.companyList);
  const { companyId } = useSelector((state) => state.onboarding);
  const { alertState } = useSelector((state) => state.alertState);

  const [currentCompanyId, setCurrentCompanyId] = useState(companyId)
  const [bots, setBots] = useState([])

  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];

  useEffect(() => {
    setUrqlTenantCompanyId(selectedCompany?.id);
  }, [selectedCompany?.id]);

  useEffect(() => {
    if (uid && email) {
      identifyHeapUser({ uid, email });
    }
  }, [uid, email]);

  useEffect(() => {
    if (selectedCompany?.id && email) {
      syncHeapCompany(selectedCompany, email);
    }
  }, [selectedCompany?.id, email, selectedCompany?.name, selectedCompany?.plan]);

  // Misma pestaña: login no dispara "storage"; forzar connection_init tras uid.
  useEffect(() => {
    if (uid) {
      reconnectGraphqlWsClient();
    }
  }, [uid]);

  useEffect(() => {
    if (companyId) setCurrentCompanyId(companyId);
  }, [companyId])

  const customer = {
    name: selectedCompany?.name || "",
    tax_id: rutWithDash(selectedCompany?.rut || ""),
    email: email || "",
  }

  const metadata = {
    userID: uid || "",
    date: moment().toString(),
    companyId: selectedCompany?.id || "",
  }

  const isAuthenticated = !!uid;
  const userHubSpot = isAuthenticated
    ? {
      user_id: uid,
      email: email,
      name: name + " " + surname,
      phone,
      company: {
        company_id: selectedCompany?.id || "",
        name: selectedCompany?.name || "",
        plan: selectedCompany?.plan && selectedCompany?.plan[0]
      },
      job_title: selectedCompany?.name || "",
      workspace_id: selectedCompany?.plan && !selectedCompany.plan.includes(Plans.blocked) && selectedCompany?.suscriptionId ? 'DE PAGO' : null,
      article_id: selectedCompany?.plan && selectedCompany?.plan[0]
    }
    : {}

  // Solo waiting-room / onboarding sin Header: evitar 2ª suscripción botlog en sesión normal.
  const SubscriptionComponent = () => {
    const res = SubcribeWS(isAuthenticated ? null : (currentCompanyId || null));

    useEffect(() => {
      if (!res?.fetching && res?.data) {
        const [bot] = res.data
        setBots((prev) => [...prev, bot])
      }
    }, [res?.data])

    return null
  }

  const HubSpotChat = ({ user }) => {
    useEffect(() => {
      // Inject HubSpot script
      const scriptId = 'hs-script-loader';
      if (!document.getElementById(scriptId)) {
        const script = document.createElement('script');
        script.src = '//js-na1.hs-scripts.com/19507197.js';
        script.async = true;
        script.defer = true;
        script.id = scriptId;
        document.body.appendChild(script);
      }

      const interval = setInterval(() => {
        if (window.HubSpotConversations?.onReady) {
          clearInterval(interval);
          window.HubSpotConversations.onReady(() => {
            window.HubSpotConversations.widget.identify({
              email: userHubSpot?.email,
              name: userHubSpot?.name,
              phone: userHubSpot?.phone,
              company: userHubSpot?.company?.name || '',
              customProperties: {
                user_id: userHubSpot?.user_id,
                company_id: userHubSpot?.company?.company_id || '',
                plan: userHubSpot?.company?.plan || '',
                workspace_id: userHubSpot?.workspace_id,
                article_id: userHubSpot?.article_id,
                job_title: userHubSpot?.job_title,
              },
            });
          });
        }
      }, 500);
    }, [userHubSpot]);

    return null;
  }

  const currentURL = window.location.hash

  if (currentURL.startsWith('#/')) {
    window.history.pushState({}, '', currentURL.replace('#', ''))
  }

  const isUserSCFO = (roles || []).includes(Roles.USER_SMARTCFO)

  
  return (
    <div className="pepperApp">
      {true && <HubSpotChat {...userHubSpot} />}
      <Alert alertState={alertState} />
      <Provider value={client}>
        <SubscriptionComponent />
        <Modal />
        <WelcomeModal />
        <NewModal>{showModal?.children}</NewModal>
        <BrowserRouter>
          <React.Suspense fallback={loading}>
            {
              process.env.REACT_APP_ISHOLDING 
                ?
                <Switch>
                  <Route
                    path="/"
                    name="Home"
                    render={() => <Holding />}
                  />
                </Switch>
                :
                !isAuthenticated ? (
                  <Switch>
                    <Route exact path="/repositorio/acceso" name="RepositoryFileAccess" render={(props) => <RepositoryFileAccess {...props} />} />
                    <Route
                      exact
                      path="/cargar_nuevo_empleado/invitar/:token"
                      name="WorkerInviteWizard"
                      render={(props) => <WorkerInviteWizard {...props} />}
                    />
                    <Redirect from="/" to="/signin" exact />
                    <Route name="Login" path="/signin" render={(props) => <SignIn {...props} />} />
                    <Route name="CreateCompany" path="/company" render={(props) => <CreateCompany {...props} />} />
                    <Route name="SIIConnect" path="/cashflow-documents" render={(props) => <ConnectSII {...props} />} />
                    <Route name="ConnectBank" path="/cashflow-bankBalance" render={(props) => <ConnectBalance {...props} />} />
                    <Route name="CashflowQuestions" path="/cashflow-additional" render={(props) => <CashFlowQuestions {...props} />} />
                    <Route name="OnboardingWaitingRoom" path="/waiting-room" render={(props) => <OnboardingWaitingRoom {...props} bots={bots} />} />
                    <Route name="ForgotPassword" path="/forgot-password" render={(props) => <ForgotPassword {...props} />} />
                    <Route name="CreatePassword" path="/create-password" render={(props) => <CreatePassword {...props} />} />
                    <Route name="ResetPassword" path="/reset-password" render={(props) => <ResetPassword {...props} />} />
                    <Route name="AutoLogin" path="/auto-login/:token" render={(props) => <AutoLogin {...props} />} />
                    <Route name="OnboardingSCFO" path="/onboarding/:stepId?" render={(props) => <OnboardingSCFO {...props} />} />
                    <Route name="DirectOnboarding" path="/onboarding-direct/:stepId?" render={(props) => <DirectOnboarding {...props} />} />
                    <Route name="SiiOnboarding" path="/onboarding-sii/:stepId?" render={(props) => <SiiOnboarding {...props} />} />
                    {/*
                      Catch-all for any protected route reached while the user is
                      not authenticated. RedirectToSignin saves the intended URL
                      (path + query) into localStorage so the user is taken back
                      to it after a successful sign-in (e.g. deep links from
                      email notifications).
                    */}
                    <Route render={() => <RedirectToSignin />} />
                  </Switch>
                ) : (
                  <Switch>
                    <Route exact path="/repositorio/acceso" name="RepositoryFileAccess" render={(props) => <RepositoryFileAccess {...props} />} />
                    <Route
                      exact
                      path="/cargar_nuevo_empleado/invitar/:token"
                      name="WorkerInviteWizard"
                      render={(props) => <WorkerInviteWizard {...props} />}
                    />
                    <Route path="/auto-login/:token" name="AutoLogin" render={(props) => <AutoLogin {...props} />} />
                    <Route path="/onboarding/:stepId?" name="OnboardingSCFO" render={(props) => <OnboardingSCFO {...props} />} />
                    <Route path="/onboarding-direct/:stepId?" name="DirectOnboarding" render={(props) => <DirectOnboarding {...props} />} />
                    <Route path="/onboarding-sii/:stepId?" name="SiiOnboarding" render={(props) => <SiiOnboarding {...props} />} />
                    <Route
                      path="/"
                      name="Home"
                      render={(props) => <Layout {...props} />}
                    />
                  </Switch>
                )}
          </React.Suspense>
        </BrowserRouter>
      </Provider>
    </div>
  );
}

export default App;
